import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { api } from '../../services/api';
import { formatDate, formatDateTime } from '../../utils/format';
import { parseTimeInput, saveErrorText, withLocalTime } from '../../utils/timeInput';
import WorkLogOverlapNotice from './WorkLogOverlapNotice';
import { inlineOverlapOf, inlineShortenPayload, inlineTimePayload } from './inlineTimeModel';

/**
 * D-292 - the start / end time of a work log, edited IN PLACE: a click on the time opens a small
 * field, Enter saves, Esc cancels, an error (an overlap, a locked month...) is shown next to the
 * field. One shared component: the work-logs table, a cycle's segments, the task page and the
 * attendance day list. It uses the existing `PATCH /work-logs/:id` (api.updateWorkLog), so what the
 * server enforces (section 21 / D-21 role rules, period lock, overlap, future) is unchanged.
 */
export interface InlineTimeLog {
  id: string;
  start_time: string | null;
  end_time?: string | null;
  service_log_locked?: boolean;
}

const hm = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'HH:mm');
};

type Which = 'start' | 'end';

export default function InlineTimeRange({ log, canEdit, onSaved, className, hideDate, floatingNotice }: {
  log: InlineTimeLog;
  /** The same gate as the edit button of the row (an internal role); the server still decides. */
  canEdit: boolean;
  onSaved?: () => void;
  className?: string;
  /** The day list already shows the day: only the times. */
  hideDate?: boolean;
  /** D-291b: a narrow cell (the day list): the overlap notice floats over the page instead of growing the cell. */
  floatingNotice?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Which | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // D-291b: the structured overlap refusal -> the shared notice (kept after the field closes/blurs).
  const [overlap, setOverlap] = useState<{ err: unknown; which: Which; payload: { start_time?: string; end_time?: string } } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const done = useRef(false); // Enter / Esc already handled -> the blur that follows must not cancel twice
  // Focus the input when it opens (was autoFocus; same timing: on mount of the editor).
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const start = log.start_time ? new Date(log.start_time) : null;
  const end = log.end_time ? new Date(log.end_time) : null;
  if (!start || Number.isNaN(start.getTime())) return <span className={className}>—</span>;
  const sameDay = !end || end.toDateString() === start.toDateString();
  const locked = !!log.service_log_locked;
  const editable = canEdit && !locked;

  const open = (which: Which) => {
    done.current = false;
    setError(null);
    setOverlap(null);
    setDraft(hm(which === 'start' ? log.start_time : log.end_time));
    setEditing(which);
  };
  const cancel = () => { setEditing(null); setError(null); };

  const save = async () => {
    if (!editing || busy) return;
    const parsed = parseTimeInput(draft);
    if (!parsed) { setError(t('inlineTime.invalid')); return; }
    const current = hm(editing === 'start' ? log.start_time : log.end_time);
    if (parsed === current) { done.current = true; cancel(); return; }
    const iso = withLocalTime(editing === 'start' ? log.start_time : log.end_time, parsed);
    if (!iso) { setError(t('inlineTime.invalid')); return; }
    await send(editing, inlineTimePayload(editing, iso));
  };

  const send = async (which: Which, payload: { start_time?: string; end_time?: string; confirm_parallel?: boolean }) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.updateWorkLog(log.id, payload);
      done.current = true;
      setEditing(null);
      setError(null);
      setOverlap(null);
      void qc.invalidateQueries({ queryKey: ['work-logs'] });
      void qc.invalidateQueries({ queryKey: ['work-records'] });
      void qc.invalidateQueries({ queryKey: ['work-log'] });
      onSaved?.();
    } catch (err) {
      if (inlineOverlapOf(err)) {
        done.current = true;
        setEditing(null);
        setError(null);
        setOverlap({ err, which, payload });
      } else {
        setError(saveErrorText(err, t('inlineTime.saveFailed')));
      }
    } finally {
      setBusy(false);
    }
  };

  const stop = (e: React.SyntheticEvent) => { e.stopPropagation(); };

  const cell = (which: Which) => {
    const iso = which === 'start' ? log.start_time : log.end_time;
    if (editing === which) {
      return (
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          dir="ltr"
          autoComplete="off"
          maxLength={5}
          value={draft}
          disabled={busy}
          aria-label={which === 'start' ? t('inlineTime.editStart') : t('inlineTime.editEnd')}
          aria-invalid={error ? true : undefined}
          data-testid={`inline-time-${which}`}
          className={`dt-above input h-7 w-16 px-1 py-0 text-center text-xs tabular-nums ${error ? 'input-error' : ''}`}
          onClick={stop}
          onChange={(e) => { setDraft(e.target.value); setError(null); }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') { e.preventDefault(); void save(); }
            else if (e.key === 'Escape') { e.preventDefault(); done.current = true; cancel(); }
          }}
          onBlur={() => { if (!done.current && !busy) cancel(); }}
        />
      );
    }
    if (!editable) return <span>{hm(iso)}</span>;
    return (
      <button
        type="button"
        className="dt-above rounded px-0.5 text-inherit underline decoration-dotted decoration-text-2/50 underline-offset-2 hover:bg-surface-subtle hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
        title={which === 'start' ? t('inlineTime.editStart') : t('inlineTime.editEnd')}
        aria-label={`${which === 'start' ? t('inlineTime.editStart') : t('inlineTime.editEnd')}: ${hm(iso)}`}
        data-testid={`inline-time-btn-${which}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); open(which); }}
        onKeyDown={stop}
      >
        {hm(iso)}
      </button>
    );
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click only stops propagation to a row link; not an interactive control
    <span className={`inline-flex flex-col ${floatingNotice ? 'relative' : ''} ${className ?? ''}`} dir="ltr" onClick={stop}>
      <span className="inline-flex items-center justify-end gap-x-1 whitespace-nowrap">
      {!hideDate && <>{formatDate(log.start_time)}{' · '}</>}
      {cell('start')}
      {end && !Number.isNaN(end.getTime()) && (
        <>
          –
          {!sameDay && <span>{formatDateTime(log.end_time).split(' ')[0]}</span>}
          {cell('end')}
        </>
      )}
      </span>
      {overlap && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click only guards row-link navigation; not an interactive control
        <div
          dir={i18n.dir()} // the surrounding span is an LTR island; the notice follows the UI language
          data-testid="inline-time-overlap"
          className={`mt-1 w-72 max-w-[80vw] whitespace-normal text-start ${floatingNotice ? 'absolute start-0 top-full z-30 rounded-lg bg-surface shadow-lg' : 'dt-above'}`}
          // Inside a row link (stretched `dt-link` / a <Link>): a click here is for the notice only. Clicks of
          // the portaled side panel bubble through React too - those are not inside this DOM node.
          onClick={(e) => { e.stopPropagation(); if (e.currentTarget.contains(e.target as Node)) e.preventDefault(); }}
        >
          <WorkLogOverlapNotice
            error={overlap.err}
            fallback={t('inlineTime.saveFailed')}
            busy={busy}
            onShorten={inlineOverlapOf(overlap.err) && inlineShortenPayload(overlap.which, inlineOverlapOf(overlap.err)!)
              ? (c) => { const p = inlineShortenPayload(overlap.which, c); if (p) void send(overlap.which, p); }
              : undefined}
            onConfirmParallel={() => void send(overlap.which, { ...overlap.payload, confirm_parallel: true })} // D-299
            onRetry={() => void send(overlap.which, overlap.payload)}
          />
          <button type="button" className="mt-1 text-xs underline" onClick={() => setOverlap(null)}>{t('common.close')}</button>
        </div>
      )}
      {error && <span role="alert" className="text-end text-xs text-danger-600" dir="auto" data-testid="inline-time-error">{error}</span>}
    </span>
  );
}
